/**
 * Copyright 2016 GeoSolutions Sas
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import ol from 'openlayers';

import ConfigUtils from './ConfigUtils';
import CoordinatesUtils from "./CoordinatesUtils";
import LocaleUtils from './LocaleUtils';

const MeasureUtils = {
    getFormattedBearingValue(azimuth = 0) {
        const format = ConfigUtils.getConfigProp("bearingFormat") || "bearing";
        const precision = ConfigUtils.getConfigProp("bearingPrecision") || 0;
        const roundToPrecision = (val) => {
            return Math.round((val + Number.EPSILON) * 10 ** precision) / 10 ** precision;
        };
        const normalizedAzimuth = ((azimuth % 360) + 360) % 360;
        if (format === "bearing") {
            let bearing = "";
            if (normalizedAzimuth >= 0 && normalizedAzimuth < 90) {
                bearing = `N ${this.degToDms(normalizedAzimuth)} E`;
            } else if (normalizedAzimuth > 90 && normalizedAzimuth <= 180) {
                bearing = `S ${this.degToDms(180 - normalizedAzimuth)} E`;
            } else if (normalizedAzimuth > 180 && normalizedAzimuth < 270) {
                bearing = `S ${this.degToDms(normalizedAzimuth - 180)} W`;
            } else if (normalizedAzimuth >= 270 && normalizedAzimuth <= 360) {
                bearing = `N ${this.degToDms(360 - normalizedAzimuth)} W`;
            }
            return bearing;
        }
        if (format === "azimuth") {
            return `${roundToPrecision(normalizedAzimuth)}°`;
        }
        if (format === "azimuth180") {
            if (normalizedAzimuth <= 180) {
                return `${roundToPrecision(normalizedAzimuth)}°`;
            } else {
                return `${roundToPrecision(normalizedAzimuth - 360)}°`;
            }
        }
        if (format === "bearingEW") {
            if (normalizedAzimuth < 180) {
                return `${roundToPrecision(normalizedAzimuth)}°E`;
            } else if (normalizedAzimuth > 180) {
                return `${roundToPrecision((360 - normalizedAzimuth))}°W`;
            } else {
                return "180°";
            }
        }
        return `${roundToPrecision(normalizedAzimuth)}°`;
    },
    formatDuration(valueSeconds) {
        return new Date(valueSeconds * 1000).toISOString().slice(11, 19);
    },
    formatMeasurement(valueMetric, isArea, unit = 'metric', decimals = -1, withUnit = true) {
        if (decimals < 0) {
            decimals = ConfigUtils.getConfigProp("measurementPrecision", null, 2);
        }
        let result = '';
        let unitlabel = unit;
        switch (unit) {
        case 'metric':
            if (isArea) {
                if (valueMetric > 1000000) {
                    result = LocaleUtils.toLocaleFixed(valueMetric / 1000000, decimals);
                    unitlabel = 'km²';
                } else if ( valueMetric > 10000) {
                    result = LocaleUtils.toLocaleFixed(valueMetric / 10000, decimals);
                    unitlabel = 'ha';
                } else {
                    result = LocaleUtils.toLocaleFixed(valueMetric, decimals);
                    unitlabel = 'm²';
                }
            } else {
                if (valueMetric > 1000) {
                    result = LocaleUtils.toLocaleFixed(valueMetric / 1000, decimals);
                    unitlabel = 'km';
                } else {
                    result = LocaleUtils.toLocaleFixed(valueMetric, decimals);
                    unitlabel = 'm';
                }
            }
            break;
        case 'imperial':
            if (isArea) {
                if (valueMetric > 2.58999 * 1000000) {
                    result = LocaleUtils.toLocaleFixed(valueMetric * 0.000000386102159, decimals);
                    unitlabel = 'mi²';
                } else if (valueMetric > 4046.86) {
                    result = LocaleUtils.toLocaleFixed(valueMetric * 0.0001, decimals);
                    unitlabel = 'acre';
                } else {
                    result = LocaleUtils.toLocaleFixed(valueMetric * 10.7639, decimals);
                    unitlabel = 'ft²';
                }
            } else {
                if (valueMetric > 1609.34) {
                    result = LocaleUtils.toLocaleFixed(valueMetric * 0.000621371, decimals);
                    unitlabel = 'mi';
                } else {
                    result = LocaleUtils.toLocaleFixed(valueMetric * 3.28084, decimals);
                    unitlabel = 'ft';
                }
            }
            break;
        case 'm':
            result = LocaleUtils.toLocaleFixed(valueMetric, decimals); break;
        case 'ft':
            result = LocaleUtils.toLocaleFixed(valueMetric * 3.28084, decimals); break;
        case 'km':
            result = LocaleUtils.toLocaleFixed(valueMetric * 0.001, decimals); break;
        case 'mi':
            result = LocaleUtils.toLocaleFixed(valueMetric * 0.000621371, decimals); break;
        case 'sqm':
            result = LocaleUtils.toLocaleFixed(valueMetric, decimals); unitlabel = 'm²'; break;
        case 'sqft':
            result = LocaleUtils.toLocaleFixed(valueMetric * 10.7639, decimals); unitlabel = 'ft²'; break;
        case 'sqkm':
            result = LocaleUtils.toLocaleFixed(valueMetric * 0.000001, decimals); unitlabel = 'km²'; break;
        case 'sqmi':
            result = LocaleUtils.toLocaleFixed(valueMetric * 0.000000386102159, decimals); unitlabel = 'mi²'; break;
        case 'ha':
            result = LocaleUtils.toLocaleFixed(valueMetric * 0.0001, decimals); break;
        case 'acre':
            result = LocaleUtils.toLocaleFixed(valueMetric * 0.000247105381467, decimals); break;
        default:
            result = LocaleUtils.toLocaleFixed(valueMetric, decimals); break;
        }
        if (withUnit) {
            result += ' ' + unitlabel;
        }
        return result;
    },
    getFormattedLength(unit, length, decimals = 2, withUnit = true) {
        let result = '';
        switch (unit) {
        case 'm':
            result = LocaleUtils.toLocaleFixed(length, decimals); break;
        case 'ft':
            result = LocaleUtils.toLocaleFixed(length * 3.28084, decimals); break;
        case 'km':
            result = LocaleUtils.toLocaleFixed(length * 0.001, decimals); break;
        case 'mi':
            result = LocaleUtils.toLocaleFixed(length * 0.000621371, decimals); break;
        default:
            result = LocaleUtils.toLocaleFixed(length, decimals); break;
        }
        if (withUnit) {
            result += ' ' + unit;
        }
        return result;
    },
    convertLength(length, fromUnit, toUnit) {
        let lengthMeters = length;
        switch (fromUnit) {
        case 'm':
            lengthMeters = length; break;
        case 'ft':
            lengthMeters = length * 0.3048; break;
        case 'km':
            lengthMeters = length * 1000; break;
        case 'mi':
            lengthMeters = length * 1609.34; break;
        default:
            lengthMeters = length; break;
        }
        switch (toUnit) {
        case 'm':
            return lengthMeters;
        case 'ft':
            return lengthMeters * 3.28084;
        case 'km':
            return lengthMeters * 0.001;
        case 'mi':
            return lengthMeters * 0.000621371;
        default:
            return lengthMeters;
        }
    },
    getFormattedArea(unit, area, decimals = 2, withUnit = true) {
        let result = '';
        let unitlabel = unit;
        switch (unit) {
        case 'sqm':
            result = LocaleUtils.toLocaleFixed(area, decimals); unitlabel = 'm²'; break;
        case 'sqft':
            result = LocaleUtils.toLocaleFixed(area * 10.7639, decimals); unitlabel = 'ft²'; break;
        case 'sqkm':
            result = LocaleUtils.toLocaleFixed(area * 0.000001, decimals); unitlabel = 'km²'; break;
        case 'sqmi':
            result = LocaleUtils.toLocaleFixed(area * 0.000000386102159, decimals); unitlabel = 'mi²'; break;
        case 'ha':
            result = LocaleUtils.toLocaleFixed(area * 0.0001, decimals); break;
        case 'acre':
            result = LocaleUtils.toLocaleFixed(area * 0.000247105381467, decimals); break;
        default:
            result = LocaleUtils.toLocaleFixed(area, decimals); break;
        }
        if (withUnit) {
            result += ' ' + unitlabel;
        }
        return result;
    },
    degToDms(deg) {
        // convert decimal deg to minutes and seconds
        const d = Math.floor(deg);
        const minfloat = (deg - d) * 60;
        const m = Math.floor(minfloat);
        const secfloat = (minfloat - m) * 60;
        const s = Math.floor(secfloat);

        return ("" + d + "° " + m + "' " + s + "'' ");
    },
    updateFeatureMeasurements(feature, geomType, featureCrs, settings) {
        const geodesic = ConfigUtils.getConfigProp("geodesicMeasurements");
        const measurements = {
            lenUnit: settings.lenUnit,
            areaUnit: settings.areaUnit
        };
        const displayCrs = settings.displayCrs ?? featureCrs;
        feature.set('label', '');
        feature.set('segment_labels', undefined);
        const geom = feature.getGeometry();
        if (geomType === 'Point') {
            feature.set('label', CoordinatesUtils.getFormattedCoordinate(geom.getCoordinates(), featureCrs, displayCrs));
        } else if (geomType === 'LineString') {
            const lengths = MeasureUtils.computeSegmentLengths(geom.getCoordinates(), featureCrs, geodesic);
            measurements.segment_lengths = lengths;
            measurements.length = lengths.reduce((sum, len) => sum + len, 0);
            feature.set('segment_labels', lengths.map(length => MeasureUtils.formatMeasurement(length, false, settings.lenUnit)));
            if (geom.getCoordinates().length > 2) {
                feature.set('end_label', MeasureUtils.formatMeasurement(measurements.length, false, settings.lenUnit));
            } else {
                feature.unset('end_label');
            }
        } else if (["Ellipse", "Polygon", "Square", "Box"].includes(geomType)) {
            const area = MeasureUtils.computeArea(geom, featureCrs, geodesic);
            const lengths = MeasureUtils.computeSegmentLengths(geom.getCoordinates()[0], featureCrs, geodesic);
            measurements.area = area;
            feature.set('label', MeasureUtils.formatMeasurement(area, true, settings.areaUnit));
            if (settings.showPerimeterLength) {
                feature.set('end_label', MeasureUtils.formatMeasurement(lengths.reduce((sum, len) => sum + len, 0), false, settings.lenUnit));
            }
        } else if (geomType === 'Circle') {
            const radius = geom.getRadius();
            measurements.radius = radius;
            feature.set('label', "r = " + MeasureUtils.formatMeasurement(radius, false, settings.lenUnit));
        } else if (geomType === 'Bearing') {
            const coo = geom.getCoordinates();
            measurements.bearing = CoordinatesUtils.calculateAzimuth(coo[0], coo[1], featureCrs);
            feature.set('label', MeasureUtils.getFormattedBearingValue(measurements.bearing));
        }
        feature.set('measurements', measurements);
    },
    computeSegmentLengths(coordinates, featureCrs, geodesic) {
        const lengths = [];
        const units = CoordinatesUtils.getUnits(featureCrs);
        if (geodesic || units === 'degrees') {
            const wgsCoo = coordinates.map(coo => CoordinatesUtils.reproject(coo, featureCrs, "EPSG:4326"));
            for (let i = 0; i < wgsCoo.length - 1; ++i) {
                lengths.push(ol.sphere.getDistance(wgsCoo[i], wgsCoo[i + 1]));
            }
        } else {
            const conv = units === 'feet' ? 0.3048 : 1;
            for (let i = 0; i < coordinates.length - 1; ++i) {
                const dx = coordinates[i + 1][0] - coordinates[i][0];
                const dy = coordinates[i + 1][1] - coordinates[i][1];
                lengths.push(Math.sqrt(dx * dx + dy * dy) * conv);
            }
        }
        return lengths;
    },
    computeArea(geometry, featureCrs, geodesic) {
        const units = CoordinatesUtils.getUnits(featureCrs);
        if (geodesic || units === 'degrees') {
            return ol.sphere.getArea(geometry, {projection: featureCrs});
        } else {
            const conv = units === 'feet' ? 0.3048 : 1;
            return geometry.getArea() * conv * conv;
        }
    },
    computeOffsetCoordinate(base, target, distance, featureCrs, geodesic) {
        const dx = target[0] - base[0];
        const dy = target[1] - base[1];
        const length = Math.sqrt(dx * dx + dy * dy);
        if (length === 0) {
            return null;
        }
        if (geodesic || CoordinatesUtils.getUnits(featureCrs) === 'degrees') {
            const azimuth = CoordinatesUtils.calculateAzimuth(base, target, featureCrs) * Math.PI / 180;
            const wgsCoo = ol.sphere.offset(CoordinatesUtils.reproject(base, featureCrs, "EPSG:4326"), distance, azimuth);
            return CoordinatesUtils.reproject(wgsCoo, "EPSG:4326", featureCrs);
        }
        const metersPerUnit = ol.proj.get(featureCrs).getMetersPerUnit();
        if (!metersPerUnit) {
            return null;
        }
        const scale = distance / metersPerUnit / length;
        return [base[0] + dx * scale, base[1] + dy * scale];
    }
};

export default MeasureUtils;
